import { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

// ---- Constants ----
const GOLD = "#0077B5";
const GOLD_DIM = "#005885";
const BG = "#F3F2EF";
const CARD = "#FFFFFF";
const BORDER = "#E1E9EE";

const TONES = [
  { id: "professional",   label: "Professional",   desc: "Polished and authoritative." },
  { id: "conversational", label: "Conversational", desc: "Warm and human." },
  { id: "bold",           label: "Bold",           desc: "Punchy and provocative." },
  { id: "educational",    label: "Educational",    desc: "Clear and value-first." },
  { id: "storytelling",   label: "Storytelling",   desc: "Narrative and personal." },
];

const PROFESSIONS = [
  { id: "lawyer",             label: "Lawyer",                            has_curated: true },
  { id: "accountant",         label: "Accountant",                        has_curated: true },
  { id: "financial_advisor",  label: "Financial Advisor",                 has_curated: true },
  { id: "real_estate",        label: "Real Estate Agent",                 has_curated: true },
  { id: "mortgage_broker",    label: "Mortgage Broker",                   has_curated: true },
  { id: "insurance_advisor",  label: "Insurance Advisor (P&C)",           has_curated: true },
  { id: "cybersecurity",      label: "Cybersecurity Professional",        has_curated: true },
  { id: "ai_professional",    label: "AI / Machine Learning Professional",has_curated: true },
  { id: "hr",                 label: "HR Professional",                   has_curated: true },
  { id: "consultant",         label: "Consultant",                        has_curated: false },
  { id: "coach",              label: "Coach",                             has_curated: false },
];

const JURISDICTIONS = [
  { id: "canada",        label: "Canada" },
  { id: "united_states", label: "United States" },
  { id: "other",         label: "Somewhere else" },
];

export default function MobileOnboard({ signupToken, onComplete }) {
  const [step, setStep] = useState(0);
  const [tokenValid, setTokenValid] = useState(null); // null=checking, true, false
  const [tokenError, setTokenError] = useState("");
  const [linkedinName, setLinkedinName] = useState("");
  const [linkedinConnecting, setLinkedinConnecting] = useState(false);
  const [selectedTone, setSelectedTone] = useState("professional");
  const [selectedProfession, setSelectedProfession] = useState("");
  const [selectedJurisdiction, setSelectedJurisdiction] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Validate signup token on mount
  useEffect(() => {
    if (!signupToken) {
      setTokenValid(false);
      setTokenError("No signup token provided. Please use the link from your text message.");
      return;
    }
    fetch(`${API_BASE}/app/api/phone/validate-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: signupToken }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(data.error || "This link has expired or is invalid.");
        }
      })
      .catch(() => {
        setTokenValid(false);
        setTokenError("Could not validate your link. Please try again.");
      });
  }, [signupToken]);

  // Check if LinkedIn was just connected (after OAuth redirect)
  useEffect(() => {
    const saved = sessionStorage.getItem("nectar_li_connected");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.connected) {
          setLinkedinName(data.linkedin_name || "");
          setStep(1); // Skip to tone picker
        }
      } catch {}
    }
  }, []);

  // ---- Handlers ----
  const handleLinkedInConnect = () => {
    setLinkedinConnecting(true);
    // Store mobile signup token so we return here after OAuth
    sessionStorage.setItem("nectar_mobile_signup_token", signupToken);
    const tempId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem("nectar_li_onboarding", "1");
    sessionStorage.removeItem("nectar_li_user_id");
    window.location.href = `${API_BASE}/app/api/auth/linkedin/connect?temp_id=${tempId}`;
  };

  const handleComplete = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/app/api/onboard/mobile-complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signup_token: signupToken,
          onboard_token: sessionStorage.getItem("nectar_onboard_token") || "",
          profession: selectedProfession,
          tone: selectedTone || "professional",
          jurisdiction: selectedJurisdiction,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        if (data.session_token) {
          localStorage.setItem("nectar_st", data.session_token);
          localStorage.setItem("nectar_token_created", String(Date.now()));
        }
        // Clean up
        sessionStorage.removeItem("nectar_mobile_signup_token");
        sessionStorage.removeItem("nectar_li_connected");
        sessionStorage.removeItem("nectar_onboard_token");
        setStep(3); // Done screen
      } else {
        setSubmitError(data.error || "Something went wrong. Please try again.");
        setSubmitting(false);
      }
    } catch {
      setSubmitError("Connection error. Please try again.");
      setSubmitting(false);
    }
  };

  // ---- Determine if profession needs jurisdiction ----
  const profObj = PROFESSIONS.find((p) => p.id === selectedProfession);
  const showJurisdiction = profObj && profObj.has_curated;

  const canSubmitProfession = selectedProfession && (!showJurisdiction || selectedJurisdiction);

  // ---- Render ----

  // Token validation loading
  if (tokenValid === null) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>BrandHives</div>
          <p style={{ color: "#666", fontSize: 15, textAlign: "center" }}>Verifying your link...</p>
        </div>
      </div>
    );
  }

  // Token invalid
  if (tokenValid === false) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>BrandHives</div>
          <div style={{ fontSize: 48, textAlign: "center", marginBottom: 16 }}>&#128279;</div>
          <h2 style={styles.heading}>Link expired</h2>
          <p style={{ color: "#666", fontSize: 14, textAlign: "center", lineHeight: 1.6 }}>
            {tokenError}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>BrandHives</div>

        {/* ---- Step 0: LinkedIn Connect ---- */}
        {step === 0 && (
          <div>
            <h2 style={styles.heading}>Connect your LinkedIn to get started</h2>
            <p style={styles.sub}>You'll be redirected to LinkedIn to authorize your account.</p>
            <button
              onClick={handleLinkedInConnect}
              disabled={linkedinConnecting}
              style={{
                ...styles.primaryBtn,
                opacity: linkedinConnecting ? 0.7 : 1,
              }}
            >
              {linkedinConnecting ? "Redirecting..." : "Connect LinkedIn"}
            </button>
            <p style={styles.hint}>We never see your password.</p>
          </div>
        )}

        {/* ---- Step 1: Pick Your Tone ---- */}
        {step === 1 && (
          <div>
            {linkedinName && (
              <p style={{ color: "#666", fontSize: 14, marginBottom: 4 }}>
                Welcome, <strong>{linkedinName.split(" ")[0]}</strong>
              </p>
            )}
            <h2 style={styles.heading}>Pick your tone</h2>
            <p style={styles.sub}>How should your LinkedIn posts sound?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {TONES.map((t) => (
                <div
                  key={t.id}
                  onClick={() => setSelectedTone(t.id)}
                  style={{
                    padding: 16,
                    border: `1px solid ${selectedTone === t.id ? GOLD : BORDER}`,
                    borderRadius: 10,
                    cursor: "pointer",
                    background: selectedTone === t.id ? "#EBF5FB" : "transparent",
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 500 }}>
                    {t.label}
                    {selectedTone === t.id && (
                      <span style={{ fontSize: 10, color: GOLD, marginLeft: 6 }}>&#10003;</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>{t.desc}</div>
                </div>
              ))}
            </div>
            <p style={styles.hint}>You can change this anytime in Settings.</p>
            <button onClick={() => setStep(2)} style={styles.primaryBtn}>
              Continue
            </button>
          </div>
        )}

        {/* ---- Step 2: Pick Your Profession ---- */}
        {step === 2 && (
          <div>
            <h2 style={styles.heading}>What's your field?</h2>
            <p style={styles.sub}>
              We'll bring you the developments that matter in your profession.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {PROFESSIONS.map((p) => (
                <div
                  key={p.id}
                  onClick={() => {
                    setSelectedProfession(p.id);
                    if (!p.has_curated) setSelectedJurisdiction("");
                  }}
                  style={{
                    padding: 16,
                    border: `1px solid ${selectedProfession === p.id ? GOLD : BORDER}`,
                    borderRadius: 10,
                    cursor: "pointer",
                    background: selectedProfession === p.id ? "#EBF5FB" : "transparent",
                    transition: "all 0.2s",
                    fontSize: 15,
                  }}
                >
                  {p.label}
                  {selectedProfession === p.id && (
                    <span style={{ fontSize: 10, color: GOLD, marginLeft: 6 }}>&#10003;</span>
                  )}
                </div>
              ))}
              <div
                onClick={() => {
                  setSelectedProfession("other");
                  setSelectedJurisdiction("");
                }}
                style={{
                  padding: 16,
                  border: `1px solid ${selectedProfession === "other" ? GOLD : BORDER}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  background: selectedProfession === "other" ? "#EBF5FB" : "transparent",
                  transition: "all 0.2s",
                  fontSize: 15,
                }}
              >
                Something else
              </div>
            </div>

            {/* Jurisdiction picker for curated professions */}
            {showJurisdiction && (
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Where do you practice?
                </h3>
                <p style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                  Rules and rulings differ by country.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {JURISDICTIONS.map((j) => (
                    <div
                      key={j.id}
                      onClick={() => setSelectedJurisdiction(j.id)}
                      style={{
                        padding: 16,
                        border: `1px solid ${selectedJurisdiction === j.id ? GOLD : BORDER}`,
                        borderRadius: 10,
                        cursor: "pointer",
                        background: selectedJurisdiction === j.id ? "#EBF5FB" : "transparent",
                        transition: "all 0.2s",
                        fontSize: 15,
                      }}
                    >
                      {j.label}
                      {selectedJurisdiction === j.id && (
                        <span style={{ fontSize: 10, color: GOLD, marginLeft: 6 }}>&#10003;</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {submitError && (
              <p style={{ color: "#DC2626", fontSize: 13, textAlign: "center", marginBottom: 8 }}>
                {submitError}
              </p>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setStep(1)}
                style={styles.secondaryBtn}
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={!canSubmitProfession || submitting}
                style={{
                  ...styles.primaryBtn,
                  flex: 1,
                  opacity: canSubmitProfession && !submitting ? 1 : 0.5,
                  cursor: canSubmitProfession && !submitting ? "pointer" : "not-allowed",
                }}
              >
                {submitting ? "Setting up..." : "Finish"}
              </button>
            </div>
          </div>
        )}

        {/* ---- Step 3: Done ---- */}
        {step === 3 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16, color: "#1D9E75" }}>&#10003;</div>
            <h2 style={styles.heading}>You're all set!</h2>
            <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
              We recommend reviewing your dashboard from your computer when you get a moment.
            </p>
            <p style={{ color: "#999", fontSize: 13 }}>
              Check your email for a link to your dashboard.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Shared inline styles ----
const styles = {
  page: {
    background: BG,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
  },
  card: {
    background: CARD,
    borderRadius: 16,
    width: "100%",
    maxWidth: 420,
    padding: "32px 24px",
  },
  logo: {
    fontFamily: "'Playfair Display',Georgia,serif",
    fontSize: 22,
    fontWeight: 700,
    color: GOLD,
    textAlign: "center",
    marginBottom: 24,
  },
  heading: {
    fontFamily: "'Playfair Display',Georgia,serif",
    fontSize: 24,
    lineHeight: 1.2,
    marginTop: 0,
    marginBottom: 8,
  },
  sub: {
    color: "#666",
    fontSize: 14,
    marginTop: 0,
    marginBottom: 20,
    lineHeight: 1.5,
  },
  hint: {
    color: "#999",
    fontSize: 12,
    textAlign: "center",
    margin: "8px 0 16px",
  },
  primaryBtn: {
    width: "100%",
    padding: 14,
    background: GOLD,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    minHeight: 44,
    fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
  },
  secondaryBtn: {
    background: "transparent",
    color: "#666",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: "14px 22px",
    fontSize: 15,
    cursor: "pointer",
    minHeight: 44,
    fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
  },
};
